
import { Component, Prop } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import VFormBuilder from '@/components/shared/form/VFormBuilder.vue';
import { InputType } from '@/enums/InputType';
import { VenueStoreStatus } from '@/enums/SmoothrPay';
import Countries from '@/mixins/Countries';
import { Venue } from '@/interfaces/models/Venue';
import {
  AttachSplitConfigurationRequest,
  CreateVenueStoreRequest,
  MerchantConfiguration,
  SplitConfiguration,
  VenueStore,
} from '@/interfaces/models/SmoothrPay';

/** Adyen caps the shopper statement at 22 characters. */
const SHOPPER_STATEMENT_MAX = 22;

/**
 * The venues this merchant serves, one Adyen store each.
 *
 * Creating a store needs an address, a phone number, the text that appears on the
 * shopper's bank statement and optionally a split-configuration profile — all
 * prefilled from the venue that was picked, so the common case is a single click.
 * An existing store's profile can be replaced afterwards through its own dialog.
 */
@Component({ components: { VFormBuilder } })
export default class VenueStores extends mixins(Countries) {
  @Prop({ type: Object, required: true }) public configuration!: MerchantConfiguration;
  @Prop({ type: Array, default: () => [] }) public stores!: VenueStore[];
  @Prop({ type: Array, default: () => [] }) public venues!: Venue[];
  @Prop({ type: Array, default: () => [] }) public splitConfigurations!: SplitConfiguration[];
  @Prop({ type: Boolean, default: false }) public canManage!: boolean;
  @Prop({ type: Boolean, default: false }) public saving!: boolean;
  @Prop({ type: Boolean, default: false }) public attaching!: boolean;

  public $refs!: {
    form: InstanceType<typeof VFormBuilder> & { form: any; validate: () => Promise<boolean> };
  };

  /**
   * Static on purpose. Handing VFormBuilder a *changing* `initialValues` makes it
   * throw its whole `form` away and rebuild it, which takes the venue selection
   * with it — the prefill writes into the live form instead (see onVenueChange).
   */
  public initialValues: Record<string, unknown> = { country: 'de' };

  public splitTarget: VenueStore | null = null;
  public splitForm: AttachSplitConfigurationRequest = { splitConfigurationId: '', balanceAccountId: '' };
  public splitErrors: { splitConfigurationId?: string; form?: string } = {};

  get closedStatus(): VenueStoreStatus {
    return VenueStoreStatus.CLOSED;
  }

  get hasBusinessLine(): boolean {
    return (this.configuration.businessLines || []).some((line) => !!line.id);
  }

  /** A profile is meaningless without a balance account to book to. */
  get canSplit(): boolean {
    return this.attachableSplitItems.length > 0;
  }

  get assignableVenues() {
    // A venue holds one store, so anything already mapped to this merchant is off
    // the table. (A venue owned by another merchant is not listed here at all;
    // the service rejects that assignment with its own message.)
    const taken = this.stores.map((store: VenueStore) => store.venue);
    return this.venues
      .filter((venue: Venue) => taken.indexOf(venue._id) === -1)
      .map((venue: Venue) => ({ text: venue.name, value: venue._id }));
  }

  get splitItems() {
    return this.splitConfigurations.map((split: SplitConfiguration) => ({
      text: split.description || split.splitConfigurationId,
      value: split.splitConfigurationId,
    }));
  }

  /** The attach endpoint replaces a profile; it cannot clear one. */
  get attachableSplitItems() {
    return this.splitItems.filter((item: { value: string }) => !!item.value);
  }

  get balanceAccountItems() {
    return (this.configuration.balanceAccounts || [])
      .filter((account) => !!account.id)
      .map((account) => ({ text: account.description || account.id, value: account.id }));
  }

  get items() {
    return [
      {
        // Autocomplete rather than Select: these lists run long, and the report
        // was that a venue could not be searched for.
        name: 'venue',
        type: InputType.Autocomplete,
        label: 'smoothrPay.stores.venue',
        items: this.assignableVenues,
        rules: 'required',
        change: (value: string) => this.onVenueChange(value),
      },
      { name: 'description', type: InputType.Text, label: 'smoothrPay.fields.description', rules: 'required' },
      {
        name: 'shopperStatement',
        type: InputType.Text,
        label: 'smoothrPay.fields.shopperStatement',
        hint: 'smoothrPay.hints.shopperStatement',
        rules: 'required|max:22',
      },
      { name: 'phoneNumber', type: InputType.Text, label: 'smoothrPay.fields.phoneNumber', rules: 'required' },
      { name: 'line1', type: InputType.Text, label: 'smoothrPay.fields.line1', rules: 'required' },
      { name: 'line2', type: InputType.Text, label: 'smoothrPay.fields.line2' },
      { name: 'postalCode', type: InputType.Text, label: 'smoothrPay.fields.postalCode', rules: 'required' },
      { name: 'city', type: InputType.Text, label: 'smoothrPay.fields.city', rules: 'required' },
      { name: 'stateOrProvince', type: InputType.Text, label: 'smoothrPay.fields.stateOrProvince' },
      {
        name: 'country',
        type: InputType.Select,
        label: 'smoothrPay.fields.country',
        items: this.countries,
        rules: 'required',
      },
      {
        name: 'splitConfigurationId',
        type: InputType.Select,
        label: 'smoothrPay.fields.splitConfiguration',
        items: this.splitItems,
        clearable: true,
        visible: this.splitItems.length > 0,
      },
      {
        name: 'balanceAccountId',
        type: InputType.Select,
        label: 'smoothrPay.fields.balanceAccount',
        items: this.balanceAccountItems,
        // Only read together with a split profile, and pointless with one account.
        visible: this.balanceAccountItems.length > 1,
      },
    ];
  }

  /**
   * Seeds the store form from the venue, which is what makes assigning a venue a
   * single click. Writes into VFormBuilder's own `form` object: those keys exist
   * from mount, so each assignment updates the widget bound to it — where
   * replacing `initialValues` would have rebuilt the object and dropped the
   * selection that triggered this.
   */
  public onVenueChange(venueId: string) {
    const form: any = this.$refs.form && this.$refs.form.form;
    const venue: any = this.venues.find((item: Venue) => item._id === venueId);
    if (!form || !venue) {
      return;
    }
    form.description = venue.name || '';
    // Adyen caps the statement, so the venue name is trimmed rather than refused.
    form.shopperStatement = (venue.name || '').slice(0, SHOPPER_STATEMENT_MAX);
    form.phoneNumber = venue.phoneNumber || '';
    form.line1 = [venue.street, venue.number].filter((part: string) => !!part).join(' ');
    form.line2 = '';
    form.postalCode = venue.postalCode || '';
    form.city = this.cityOf(venue);
    form.stateOrProvince = venue.federalState || '';
    form.country = venue.country || 'de';
  }

  public storeColor(store: VenueStore): string {
    switch (store.status) {
      case VenueStoreStatus.ACTIVE:
        return 'success';
      case VenueStoreStatus.PENDING:
        return 'info';
      case VenueStoreStatus.CLOSED:
        return 'error';
      default:
        return 'grey';
    }
  }

  public venueName(id: string): string {
    const venue = this.venues.find((item: Venue) => item._id === id);
    return venue ? venue.name : id;
  }

  /** The profile's description reads better than its Adyen id. */
  public splitLabel(store: VenueStore): string {
    const id = store.adyen && store.adyen.splitConfigurationId;
    if (!id) {
      return '—';
    }
    const known = this.splitConfigurations.find((split: SplitConfiguration) => split.splitConfigurationId === id);
    return known && known.description ? known.description : id;
  }

  public openSplitEditor(store: VenueStore) {
    this.splitTarget = store;
    this.splitErrors = {};
    this.splitForm = {
      splitConfigurationId: (store.adyen && store.adyen.splitConfigurationId) || '',
      balanceAccountId: (store.adyen && store.adyen.balanceAccountId) || '',
    };
  }

  public closeSplitEditor() {
    this.splitTarget = null;
  }

  public attachSplit() {
    const store = this.splitTarget;
    if (!store) {
      return;
    }
    this.splitErrors = {};
    if (!this.splitForm.splitConfigurationId) {
      this.splitErrors = { splitConfigurationId: this.$t('smoothrPay.stores.splitDialog.required') as string };
      return;
    }
    // Re-sending what the store already has would be a pointless Adyen call.
    const sameProfile = this.splitForm.splitConfigurationId === (store.adyen && store.adyen.splitConfigurationId);
    const currentAccount = (store.adyen && store.adyen.balanceAccountId) || '';
    const sameAccount = (this.splitForm.balanceAccountId || '') === currentAccount;
    if (sameProfile && sameAccount) {
      this.splitErrors = { form: this.$t('smoothrPay.stores.splitDialog.noChanges') as string };
      return;
    }

    const payload: AttachSplitConfigurationRequest = {
      splitConfigurationId: this.splitForm.splitConfigurationId,
    };
    // Omitted lets the service pick the merchant's first balance account.
    if (this.splitForm.balanceAccountId) {
      payload.balanceAccountId = this.splitForm.balanceAccountId;
    }
    this.$emit('attach-split', { venue: store.venue, payload });
    this.closeSplitEditor();
  }

  public async assign() {
    const valid = await this.$refs.form.validate();
    if (!valid) {
      return;
    }
    const form: any = this.$refs.form.form;
    const payload: CreateVenueStoreRequest = {
      venue: form.venue,
      merchantConfigurationId: this.configuration._id,
      description: form.description,
      phoneNumber: form.phoneNumber,
      shopperStatement: form.shopperStatement,
      address: {
        country: form.country,
        line1: form.line1,
        postalCode: form.postalCode,
        city: form.city,
      },
    };
    if (form.line2) {
      payload.address.line2 = form.line2;
    }
    if (form.stateOrProvince) {
      payload.address.stateOrProvince = form.stateOrProvince;
    }
    if (form.splitConfigurationId) {
      payload.splitConfigurationId = form.splitConfigurationId;
      if (form.balanceAccountId) {
        payload.balanceAccountId = form.balanceAccountId;
      }
    }
    this.$emit('assign', payload);
  }

  /** `city` is a localization map on the venue even where the type says string. */
  private cityOf(venue: any): string {
    const city = venue.city;
    if (!city) {
      return '';
    }
    if (typeof city === 'string') {
      return city;
    }
    return city[this.$i18n.locale] || city.de || city.en || Object.values(city)[0] || '';
  }
}
